import React, { Fragment, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { styled, SxProp, View } from 'dripsy';
import { useNavigation } from '@react-navigation/native';

import {
  EmptyResultView,
  FriendshipUserList,
  Separator,
} from '@components';
import {
  PersonalizationNavigatorNavigationPropType,
  screens,
} from '@navigation';
import { SuggestedUsersListHeader } from './SuggestedUsersListHeader';
import { SuggestedUsersListFooter } from './SuggestedUsersListFooter';
import type { SuggestedUserList } from '@models/Viewer/SuggestedUserListModel';
import type { User } from '@models/Viewer/UserModel';

function contentContainerStyle(props: EdgeInsets): SxProp {
  return (theme) => ({
    flexGrow: 1,
    width: ['100%', '80%'],
    maxWidth: [null, 495],
    px: ['$6', null],
    pt: theme.space.$8 + props.top,
    pb: theme.space.$6 + props.bottom,
    alignSelf: 'center',
    justifyContent: [null, 'center'],
  });
}

const EmptyResultViewContainer = styled(View)({
  flex: 1,
  maxHeight: [null, 840],
});

interface ISuggestedUsersListProps {
  list: SuggestedUserList;
}

type NavigationPropType =
  PersonalizationNavigatorNavigationPropType<'UsersSuggestion'>;

export const SuggestedUsersList = observer<ISuggestedUsersListProps>(
  ({ list }) => {
    const insets = useSafeAreaInsets();
    const nav = useNavigation<NavigationPropType>();

    const navigateToUser = useCallback((item: User) => {
      nav.push(screens.UserProfileTabNavigator, {
        id: item.id,
      });
    }, []);

    const isLoading = list.fetch.inProgress && list.isEmpty;

    return (
      <FriendshipUserList
        contentContainerSx={contentContainerStyle(insets)}
        data={list.asArray}
        isLoading={isLoading}
        onPress={navigateToUser}
        isShowLocation={true}
        ListHeaderComponent={() => (
          <Fragment>
            <SuggestedUsersListHeader />
            <Separator size={['$7', '$8']} />
          </Fragment>
        )}
        ListFooterComponent={() => (
          <Fragment>
            <Separator size="$7" />
            <SuggestedUsersListFooter />
          </Fragment>
        )}
        ListEmptyComponent={() => (
          <EmptyResultViewContainer>
            <EmptyResultView />
          </EmptyResultViewContainer>
        )}
      />
    );
  },
);
